import {SortingState, Updater} from '@tanstack/table-core';
import clsx from 'clsx';
import {throttle} from 'lodash';
import {
  MaterialReactTable,
  MRT_RowData,
  MRT_RowVirtualizer,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnSizingState,
} from 'material-react-table';
import React, {RefObject, useCallback, useEffect, useMemo, useRef} from 'react';
import {ArrowDownward} from '@mui/icons-material';
import {designSystemToken} from '@lightricks/react-design-system';
import InfiniteLoader from '@/components/infinite-loader';
import styles from './Table.module.scss';

// Trigger pagination when the remaining distance to the bottom is less than
// 80% of the container's visible height, so the next page loads early.
const END_REACHED_RATIO = 0.8;

const styleDefinitions = {
  muiTableProps: {
    className: styles.tableContainer,
  },
  muiTableHeadProps: {
    className: styles.tableHead,
  },
  muiTableHeadRowProps: {
    className: styles.tableHeadRow,
  },
  muiTableHeadCellProps: {
    className: styles.tableHeadCell,
  },
  muiTableBodyProps: {
    className: styles.tableBody,
  },
  muiTableBodyRowProps: {
    className: styles.tableBodyRow,
    disabled: styles.tableBodyRowDisabled,
  },
  muiTableBodyCellProps: {
    className: styles.tableBodyCell,
  },
  muiTableBodyFirstRowProps: {
    className: styles.tableBodyFirstRow,
  },
};

type TableClasses = {
  container?: string;
  paper?: string;
  footer?: string;
  tableHeadRow?: string;
  tableBodyRow?: string;
};

type TableProps<T extends MRT_RowData> = {
  data: T[];
  columns: MRT_ColumnDef<T>[];
  isLoadingInitial?: boolean;
  isLoading?: boolean;
  showProgressBars: boolean;
  visibleColumns: Record<string, boolean>;
  rowSelection: Record<string, boolean>;
  sorting: SortingState;
  onSorting?: (sorting: SortingState) => void;
  onRowClick?: (row: T) => void;
  ignorePortalRowClicks?: boolean;
  onRowHover?: (row: T | null) => void;
  isRowDisabled?: (row: T) => boolean;
  isRowTransitioned?: (row: T) => boolean;
  testID?: string;
  classes?: TableClasses;
  onEndReached?: () => void;
  enableVirtualization?: boolean;
  enableSorting?: boolean;
  manualSorting?: boolean;
  rowVirtualizerRef?: RefObject<MRT_RowVirtualizer>;
  tableScrollTopKey?: string;
  tableRef?: RefObject<HTMLTableElement>;
  isFirstRowBorder?: boolean;
  columnOrder?: string[];
  selectedRowBackgroundColor?: string;
  /**
   * Optional set of row ids that should be visually marked as "currently active"
   * (e.g. rows whose details are open in a preview side panel). When provided,
   * matching rows receive a distinct background color. Other row states
   * (hover, multi-select) are unaffected.
   */
  highlightedRowIds?: Set<string>;
  /**
   * Optional set of row ids for a softer "last viewed" highlight (e.g. after
   * a preview panel closes). Open/highlighted styling takes precedence.
   */
  lastViewedRowIds?: Set<string>;
  emptyStateComponent?: React.ReactNode;
  onScroll?: (event: React.UIEvent<HTMLElement>) => void;
  tabIndex?: number;
  renderBottomContent?: () => React.ReactNode;
  enableColumnResizing?: boolean;
  columnSizing?: MRT_ColumnSizingState;
  onColumnSizingChange?: (sizing: MRT_ColumnSizingState) => void;
};

export function applyUpdater<T>(currentState: T, updater: Updater<T>): T {
  let newState: T;
  if (typeof updater === 'function') {
    const updateFn = updater as (old: T) => T;
    newState = updateFn(currentState);
  } else {
    newState = updater;
  }
  return newState;
}

function Table<T extends MRT_RowData>(props: TableProps<T>) {
  const {
    data,
    columns,
    rowSelection,
    isLoadingInitial,
    isLoading,
    showProgressBars,
    visibleColumns,
    sorting,
    onSorting,
    onRowClick,
    ignorePortalRowClicks = false,
    onRowHover,
    isRowDisabled,
    isRowTransitioned,
    testID = 'table',
    classes = {},
    onEndReached,
    selectedRowBackgroundColor,
    enableVirtualization = false,
    enableSorting = false,
    manualSorting = true,
    rowVirtualizerRef,
    tableScrollTopKey = 'tableScrollTop',
    tableRef,
    isFirstRowBorder = true,
    columnOrder = undefined,
    emptyStateComponent,
    onScroll,
    tabIndex,
    renderBottomContent,
    highlightedRowIds,
    lastViewedRowIds,
    enableColumnResizing = false,
    columnSizing,
    onColumnSizingChange,
  } = props;

  const showLoader = !isLoadingInitial && isLoading;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const newRowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const rowVirtualizerInstanceRef =
    rowVirtualizerRef || newRowVirtualizerInstanceRef;

  const handleOnScroll = useCallback(
    (event?: React.UIEvent<HTMLElement>) => {
      if (event?.target) {
        const target = event.target as HTMLDivElement;
        if (enableVirtualization && tableScrollTopKey) {
          sessionStorage.setItem(
            tableScrollTopKey,
            target.scrollTop.toString()
          );
        }
      }
    },
    [enableVirtualization, tableScrollTopKey]
  );

  const throttledHandleOnScroll = useMemo(
    () => throttle(handleOnScroll, 100),
    [handleOnScroll]
  );

  useEffect(() => {
    return () => throttledHandleOnScroll.cancel();
  }, [throttledHandleOnScroll]);

  const handleScrollEvent = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      throttledHandleOnScroll(event);
      onScroll?.(event);
    },
    [throttledHandleOnScroll, onScroll]
  );

  // Detect when the user scrolls near the bottom to trigger pagination.
  // Listens on the table container when it scrolls, otherwise on the closest
  // scrollable ancestor (e.g. the page-level container).
  useEffect(() => {
    if (!onEndReached || !tableContainerRef.current) return undefined;

    const container = tableContainerRef.current;
    const isContainerScrollable =
      container.scrollHeight > container.clientHeight;

    let scrollTarget: HTMLElement;
    if (isContainerScrollable) {
      scrollTarget = container;
    } else {
      let parent: HTMLElement | null = container.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        if (
          parent.scrollHeight > parent.clientHeight &&
          (style.overflowY === 'auto' || style.overflowY === 'scroll')
        ) {
          break;
        }
        parent = parent.parentElement;
      }
      if (!parent) return undefined;
      scrollTarget = parent;
    }

    const handleScroll = () => {
      const {scrollHeight, scrollTop, clientHeight} = scrollTarget;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceToBottom <= clientHeight * END_REACHED_RATIO) {
        onEndReached();
      }
    };

    scrollTarget.addEventListener('scroll', handleScroll, {passive: true});
    return () => scrollTarget.removeEventListener('scroll', handleScroll);
  }, [onEndReached]);
  // After data changes in virtualized tables, re-measure the virtualizer so
  // new rows render immediately. Also re-run when loading finishes — the
  // initial measure can be skipped while fetches are in flight, leaving rows
  // at the 1px fallback height from rowVirtualizerOptions.
  useEffect(() => {
    let frame: number | undefined;

    if (
      enableVirtualization &&
      data.length > 0 &&
      !isLoadingInitial &&
      !isLoading
    ) {
      frame = requestAnimationFrame(() => {
        rowVirtualizerInstanceRef.current?.measure?.();
      });
    }

    return () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
    };
  }, [
    data.length,
    enableVirtualization,
    isLoadingInitial,
    isLoading,
    rowVirtualizerInstanceRef,
  ]);

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSortingState = applyUpdater(sorting, updater);
      onSorting?.(newSortingState);
    },
    [sorting, onSorting]
  );

  const handleColumnSizingChange = useCallback(
    (updater: Updater<MRT_ColumnSizingState>) => {
      const newColumnSizing = applyUpdater(columnSizing ?? {}, updater);
      onColumnSizingChange?.(newColumnSizing);
    },
    [columnSizing, onColumnSizingChange]
  );

  const defaultOptions = useMemo(
    () => ({
      enableGlobalFilterModes: false,
      enableStickyHeader: true,
      enableColumnActions: false,
      enableTopToolbar: false,
      enableBottomToolbar: false,
      manualPagination: true,
      mrtTheme: (theme: any) => ({
        baseBackgroundColor: designSystemToken('semantic.bg.primary'),
        selectedRowBackgroundColor: designSystemToken('semantic.bg.tertiary'),
        draggingBorderColor: designSystemToken('semantic.fg.tertiary'),
      }),
      muiTablePaperProps: {elevation: 0},
      icons: {
        SyncAltIcon: () =>
          enableSorting ? (
            <div className={styles.syncIcon}>
              <ArrowDownward fontSize="inherit" />
            </div>
          ) : (
            <div />
          ),
      },
    }),
    [enableSorting]
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      // Ignore clicks that originated outside the row's DOM subtree (e.g. portals like MUI modals/menus).
      // React bubbles portal events through the React tree even though the DOM target is elsewhere.
      if (
        ignorePortalRowClicks &&
        !e.currentTarget.contains(e.target as Node)
      ) {
        return;
      }
      const rowId = (e.currentTarget as HTMLTableRowElement).id;
      const original = data.find((r) => (r as {id?: string})?.id === rowId);
      if (original) {
        onRowClick?.(original as T);
      }
    },
    [data, ignorePortalRowClicks, onRowClick]
  );

  const handleRowMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      const rowId = (e.currentTarget as HTMLTableRowElement).id;
      const original = data.find((r) => (r as {id?: string})?.id === rowId);
      onRowHover?.(original ?? null);
    },
    [data, onRowHover]
  );

  const handleRowMouseLeave = useCallback(() => {
    onRowHover?.(null);
  }, [onRowHover]);

  const muiTableBodyRowProps = useCallback(
    ({row}: {row: {id: string; getIsSelected: () => boolean; original: T}}) => {
      const {className, disabled: disabledClassName} =
        styleDefinitions.muiTableBodyRowProps;
      const {className: firstRowClassName} =
        styleDefinitions.muiTableBodyFirstRowProps;
      const isDisabled = isRowDisabled?.((row as MRT_RowData).original);
      const isTransitioned = isRowTransitioned?.((row as MRT_RowData).original);
      const isSelected = row.getIsSelected();
      const isHighlighted = highlightedRowIds?.has(row.id) ?? false;
      const isLastViewed = lastViewedRowIds?.has(row.id) ?? false;
      return {
        'id': row.id,
        'className': clsx(
          className,
          isDisabled && disabledClassName,
          isTransitioned && styles.tableBodyRowTransitioned,
          isHighlighted && styles.tableBodyRowHighlighted,
          isLastViewed && styles.tableBodyRowLastViewed,
          classes.tableBodyRow,
          isFirstRowBorder && firstRowClassName
        ),
        'aria-disabled': isDisabled,
        'onClick': handleRowClick,
        'onMouseEnter': handleRowMouseEnter,
        'onMouseLeave': handleRowMouseLeave,
        'sx': {
          ...(isSelected &&
            selectedRowBackgroundColor && {
              backgroundColor: selectedRowBackgroundColor,
            }),
        },
      } as React.HTMLAttributes<HTMLTableRowElement> & {sx?: object};
    },
    [
      classes.tableBodyRow,
      isFirstRowBorder,
      isRowDisabled,
      isRowTransitioned,
      selectedRowBackgroundColor,
      highlightedRowIds,
      lastViewedRowIds,
      handleRowClick,
      handleRowMouseEnter,
      handleRowMouseLeave,
    ]
  );

  const rowVirtualizerOptions = useMemo(
    () =>
      enableVirtualization
        ? {
            initialOffset:
              Number(sessionStorage.getItem(tableScrollTopKey)) || 0,
            initialRect: {
              height: 1,
              width: 0,
            },
          }
        : undefined,
    [enableVirtualization, tableScrollTopKey]
  );

  const muiTableFooterProps = useMemo(
    () => ({className: classes.footer}),
    [classes.footer]
  );

  const muiTablePaperProps = useMemo(
    () => ({
      classes: {root: classes.paper},
      ...defaultOptions.muiTablePaperProps,
    }),
    [classes.paper, defaultOptions.muiTablePaperProps]
  );

  const muiTableContainerProps = useMemo(
    () => ({
      classes: {root: classes.container},
      ref: tableContainerRef,
      ...(tabIndex != null && {tabIndex}),
      sx: {maxHeight: '100%'},
      onScroll: handleScrollEvent,
    }),
    [classes.container, handleScrollEvent, tabIndex]
  );

  const muiTableProps = useMemo(
    () => ({
      ...styleDefinitions.muiTableProps,
      'ref': tableRef,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      'data-testid': testID,
    }),
    [tableRef, testID]
  );

  const muiTableHeadRowProps = useMemo(
    () => ({classes: {root: classes.tableHeadRow}}),
    [classes.tableHeadRow]
  );

  const table = useMaterialReactTable({
    columns,
    data,
    getRowId: (row) => (row as T & {id?: string})?.id ?? '',
    state: {
      isLoading: isLoadingInitial,
      showProgressBars,
      columnVisibility: visibleColumns,
      rowSelection,
      sorting,
      ...(columnOrder && {columnOrder}),
      ...(onColumnSizingChange && columnSizing !== undefined && {columnSizing}),
    },
    onSortingChange,
    manualSorting,
    layoutMode: enableColumnResizing ? 'grid-no-grow' : 'grid',
    ...(enableColumnResizing && {
      enableColumnResizing: true,
      columnResizeMode: 'onChange' as const,
      ...(onColumnSizingChange && {
        onColumnSizingChange: handleColumnSizingChange,
      }),
    }),
    renderEmptyRowsFallback: () => emptyStateComponent,
    ...defaultOptions,
    ...styleDefinitions,
    muiTableFooterProps,
    muiTableBodyRowProps,
    muiTablePaperProps,
    muiTableContainerProps,
    muiTableProps,
    muiTableHeadRowProps,
    enableRowVirtualization: enableVirtualization,
    enablePagination: !enableVirtualization,
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions,
    ...(renderBottomContent && {
      enableBottomToolbar: true,
      renderBottomToolbar: () => renderBottomContent(),
    }),
  });
  return (
    <>
      <MaterialReactTable table={table} />
      <InfiniteLoader isLoading={!!showLoader} />
    </>
  );
}

export default Table;
